import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { slotErrorMessage } from "../../../utils/helpers/slotErrors";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../../utils/contexts/UserContext";
import { getActiveRubric } from "../../../services/rubricService";
import {
  cancelObservation,
  confirmObservation,
  declineObservation,
  getObservation,
  getObservations,
  getUpcomingObservations,
  updateObservation,
} from "../../../services/observationService";
import {
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../../../services/notificationService";
import { getTeacherTimetable } from "../../../services/timetableService";
import { getGroups } from "../../../services/groupService";
import { MAX_PAGE_SIZE } from "../../../utils/constants/constants";
import {
  DIRECTION,
  FINALIZED_STATUSES,
  NOTIFICATION_TYPE,
} from "../../../utils/constants/observations";
import {
  DISPLAYED_OBSERVATION_LIMIT,
  computeOverallScoreFromApi,
  selectCountedObservations,
} from "../../../utils/helpers/scoring";
import { scoreTier } from "../../../utils/helpers/scoreColors";
import { inferGradesFromObservations } from "../../../utils/helpers/teacherUtils";
import {
  formatRelativeTime,
  formatShortDate,
} from "../../../utils/helpers/dateDisplay";
import { fullName } from "../../../utils/helpers/userDisplay";
import Avatar from "../../../components/Avatar/Avatar";
import PerformanceOverviewTab from "./PerformanceOverviewTab";
import UpcomingObservationsTab from "./UpcomingObservationsTab";
import RequestVisitModal from "./RequestVisitModal";
import ObservationDetailModal from "./ObservationDetailModal";
import Skeleton, { SkeletonCard } from "../../../components/Skeleton/Skeleton";
import { decorateUpcoming } from "./upcomingHelpers";

const FINALIZED_STATUS_PARAM = [...FINALIZED_STATUSES].join(",");

// null score = no counted observations: muted ring, no false red zero.
const scoreColors = (score) => {
  const tier = scoreTier(score);
  return tier
    ? { glow: tier.glowClass, stroke: tier.strokeClass }
    : { glow: "bg-slate-500", stroke: "text-white/20" };
};

function NotificationsDropdown({
  notifications,
  unreadCount,
  onNotificationClick,
  onMarkAllRead,
  onClose,
}) {
  const { t } = useTranslation();
  return (
    <>
      <div className="fixed inset-0 z-[110]" onClick={onClose} />
      <div className="fixed inset-x-4 top-20 w-auto sm:absolute sm:inset-x-auto sm:top-auto sm:right-0 sm:mt-4 sm:w-80 bg-white rounded-3xl shadow-2xl border border-slate-200 z-[120] overflow-hidden">
        <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">
            {t("teacher.notifications")}
          </h3>
          {unreadCount > 0 && (
            <button
              onClick={onMarkAllRead}
              className="text-[10px] font-black text-indigo-600 uppercase tracking-widest hover:text-indigo-800 whitespace-nowrap text-left"
            >
              {t("teacher.markAllRead")}
            </button>
          )}
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          {notifications.length > 0 ? (
            <div className="divide-y divide-slate-50">
              {notifications.map((n) => (
                <div
                  key={n.uuid}
                  onClick={() => onNotificationClick(n)}
                  className={`p-5 hover:bg-slate-50 transition-colors cursor-pointer relative ${
                    !n.is_read ? "bg-indigo-50/30" : ""
                  }`}
                >
                  {!n.is_read && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-500" />
                  )}
                  <div className="flex gap-4">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${notificationChipClass(n.type)}`}
                    >
                      <NotificationIcon type={n.type} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-black text-slate-900 mb-1">{n.title}</p>
                      <p className="text-[11px] text-slate-500 leading-relaxed mb-2">
                        {n.message}
                      </p>
                      <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">
                        {formatRelativeTime(n.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-10 text-center">
              <p className="text-xs text-slate-400 font-bold">{t("teacher.noNotifications")}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

const NOTIFICATION_CHIP_CLASS = {
  [NOTIFICATION_TYPE.PEER_VISIT_REQUESTED]: "bg-amber-100 text-amber-600",
  [NOTIFICATION_TYPE.PEER_VISIT_CONFIRMED]: "bg-emerald-100 text-emerald-600",
  [NOTIFICATION_TYPE.PEER_VISIT_DECLINED]: "bg-rose-100 text-rose-600",
  [NOTIFICATION_TYPE.VISIT_RESCHEDULED]: "bg-indigo-100 text-indigo-600",
  [NOTIFICATION_TYPE.VISIT_CANCELLED]: "bg-rose-100 text-rose-600",
  [NOTIFICATION_TYPE.OFFICIAL_VISIT_SCHEDULED]: "bg-amber-100 text-amber-600",
  [NOTIFICATION_TYPE.VISIT_ASSIGNED]: "bg-amber-100 text-amber-600",
  [NOTIFICATION_TYPE.OBSERVATION_SHARED]: "bg-indigo-100 text-indigo-600",
  [NOTIFICATION_TYPE.OBSERVATION_SUBMITTED]: "bg-blue-100 text-blue-600",
  [NOTIFICATION_TYPE.SUPPORT_REPLY]: "bg-violet-100 text-violet-600",
};
const DEFAULT_CHIP_CLASS = "bg-blue-100 text-blue-600";

const CHECK_ICON_PATH = "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z";
const NOTIFICATION_ICON_PATH = {
  [NOTIFICATION_TYPE.PEER_VISIT_REQUESTED]:
    "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
  [NOTIFICATION_TYPE.OFFICIAL_VISIT_SCHEDULED]:
    "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
  // A person, not a calendar: this one says the visit is now YOURS to run,
  // which is the part the date on the card does not carry.
  [NOTIFICATION_TYPE.VISIT_ASSIGNED]:
    "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
  [NOTIFICATION_TYPE.VISIT_RESCHEDULED]:
    "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15",
  [NOTIFICATION_TYPE.PEER_VISIT_DECLINED]:
    "M6 18L18 6M6 6l12 12",
  [NOTIFICATION_TYPE.VISIT_CANCELLED]:
    "M6 18L18 6M6 6l12 12",
  // A speech bubble: someone answered you, as opposed to a visit event.
  [NOTIFICATION_TYPE.SUPPORT_REPLY]:
    "M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z",
};

const notificationChipClass = (type) =>
  NOTIFICATION_CHIP_CLASS[type] || DEFAULT_CHIP_CLASS;

function NotificationIcon({ type }) {
  const d = NOTIFICATION_ICON_PATH[type] || CHECK_ICON_PATH;
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={d} />
    </svg>
  );
}

export default function TeacherDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [observations, setObservations] = useState([]);
  const [observationTotal, setObservationTotal] = useState(null);
  const [observationDetails, setObservationDetails] = useState([]);
  const [rubric, setRubric] = useState(null);
  const [upcomingRaw, setUpcomingRaw] = useState([]);
  const [timetable, setTimetable] = useState([]);
  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [observationsError, setObservationsError] = useState(false);

  const [activeTab, setActiveTab] = useState("overview");
  const [expandedStandardId, setExpandedStandardId] = useState(null);
  const [showFocusAreas, setShowFocusAreas] = useState(false);
  const [calendarViewMode, setCalendarViewMode] = useState("calendar");
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedObservation, setSelectedObservation] = useState(null);
  const [requestModal, setRequestModal] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const loadObservations = useCallback(() => {
    if (!user?.uuid) return undefined;
    const controller = new AbortController();
    setIsLoading(true);
    setObservationsError(false);

    const load = async () => {
      const [listData, rubricData] = await Promise.all([
        getObservations({
          params: {
            observed_teacher__uuid: user.uuid,
            status__in: FINALIZED_STATUS_PARAM,
            ordering: "-observed_at",
          },
          signal: controller.signal,
        }),
        getActiveRubric({ signal: controller.signal }),
      ]);
      if (controller.signal.aborted) return;
      const list = listData?.results || [];
      setObservations(list);
      // True finalized total from the paginated envelope — `list` is page 1
      // only, so its length caps at the backend page size.
      setObservationTotal(listData?.count ?? null);
      setRubric(rubricData);

      const recent = list.slice(0, DISPLAYED_OBSERVATION_LIMIT);
      if (recent.length === 0) {
        setObservationDetails([]);
        return;
      }
      const details = await Promise.all(
        recent.map((o) =>
          getObservation({ uuid: o.uuid, signal: controller.signal }).catch(
            () => null,
          ),
        ),
      );
      if (controller.signal.aborted) return;
      setObservationDetails(details.filter(Boolean));
    };

    load()
      .catch((err) => {
        if (err?.code === "ERR_CANCELED") return;
        // Distinguish a load failure from a genuinely empty history so the
        // dashboard doesn't silently render 0%/"—" as if never observed.
        setObservations([]);
        setObservationDetails([]);
        setObservationsError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [user?.uuid]);

  useEffect(() => loadObservations(), [loadObservations]);

  const loadUpcoming = useCallback(() => {
    if (!user?.uuid) return undefined;
    const controller = new AbortController();
    getUpcomingObservations({
      // A booking whose day has passed is the one this tab most needs to show:
      // it is the only surface where the observer who owes it can start it.
      params: { include_overdue: true },
      signal: controller.signal,
    })
      .then((data) => setUpcomingRaw(data?.results || []))
      .catch((err) => {
        if (err?.code === "ERR_CANCELED") return;
        setUpcomingRaw([]);
      });
    return () => controller.abort();
  }, [user?.uuid]);

  useEffect(() => loadUpcoming(), [loadUpcoming]);

  const loadTimetable = useCallback(() => {
    if (!user?.uuid) return undefined;
    const controller = new AbortController();
    getTeacherTimetable({ params: { teacher: user.uuid }, signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted) return;
        setTimetable(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (controller.signal.aborted || err?.code === "ERR_CANCELED") return;
        // Keep the prior timetable on a refetch failure — a successful lesson
        // write followed by a failed refetch must not blank the whole calendar
        // (which would read as "you have no lessons"). Initial state is already
        // [], so a first-load failure still shows the empty state.
      });
    return () => controller.abort();
  }, [user?.uuid]);

  useEffect(() => loadTimetable(), [loadTimetable]);

  const orgUuid = user?.organizations?.[0]?.organization?.uuid || null;
  useEffect(() => {
    if (!orgUuid) return undefined;
    const controller = new AbortController();
    getGroups({ params: { organization: orgUuid, page_size: MAX_PAGE_SIZE }, signal: controller.signal })
      .then((data) => setGroups(data?.results || []))
      .catch((err) => {
        if (err?.code === "ERR_CANCELED") return;
        setGroups([]);
      });
    return () => controller.abort();
  }, [orgUuid]);

  const upcoming = useMemo(
    () => upcomingRaw.map((o) => decorateUpcoming(o, user?.uuid)),
    [upcomingRaw, user?.uuid],
  );

  const overallRaw = computeOverallScoreFromApi(observations);
  const overallScore = overallRaw == null ? null : Math.round(overallRaw);
  // Same freshness semantics as the teacher overview modal: emerald while the
  // last observation still counts toward the score, amber once it has aged out.
  const isLastObservationFresh =
    selectCountedObservations(observations).length > 0;

  const lastObservationDate = useMemo(() => {
    const finalised = observations
      .filter((o) => FINALIZED_STATUSES.has(o.status) && o.observed_at)
      .sort((a, b) => new Date(b.observed_at) - new Date(a.observed_at));
    return finalised.length ? formatShortDate(finalised[0].observed_at) : "—";
  }, [observations]);

  // Primary staff link (backend orders organizations active-first).
  const primaryOrg = user?.organizations?.[0];
  const employedSince = primaryOrg?.start_date
    ? new Date(primaryOrg.start_date).getFullYear()
    : "—";
  const subject =
    (primaryOrg?.subjects || []).map((s) => s.name).join(", ") || "—";
  const department = primaryOrg?.department?.name || null;
  const colors = scoreColors(overallScore);
  const unreadCount = notifications.filter((n) => !n.is_read).length;
  const peersVisitedCount = upcoming.filter(
    (o) => o.direction === DIRECTION.OUTGOING,
  ).length;

  const patchUpcoming = useCallback((updated) => {
    setUpcomingRaw((prev) =>
      prev
        .map((o) => (o.uuid === updated.uuid ? updated : o))
        // Own declines stay (rose chip + undo in the calendar) until the next
        // refetch; only requester-withdrawn visits drop immediately.
        .filter((o) => o.status !== "cancelled" || o.closure === "declined"),
    );
  }, []);

  // These three show the app's OWN wording rather than the server's `detail`.
  // DRF always fills `detail` on a PermissionDenied, so `detail || t(...)` never
  // reached the translation — every failure here arrived in English, in a
  // Russian UI, and the Kazakh string was unreachable code. What the server adds
  // ("Only the observed teacher can confirm") is a rule the UI should not have
  // offered the button under anyway.
  const handleConfirmObservation = useCallback(
    async (uuid) => {
      try {
        const updated = await confirmObservation({ uuid });
        toast.success(t("teacher.visitConfirmed"));
        patchUpcoming(updated);
        setSelectedObservation(null);
      } catch (err) {
        toast.error(t("teacher.confirmFailed"));
      }
    },
    [patchUpcoming, t],
  );

  const handleDeclineObservation = useCallback(
    async (uuid) => {
      try {
        const updated = await declineObservation({ uuid });
        toast.success(t("teacher.visitDeclined"));
        patchUpcoming(updated);
        setSelectedObservation(null);
      } catch (err) {
        toast.error(t("teacher.declineFailed"));
      }
    },
    [patchUpcoming, t],
  );

  const handleCancelObservation = useCallback(
    async (uuid, reason) => {
      try {
        const updated = await cancelObservation({ uuid, body: { reason } });
        toast.success(t("upcoming.visitCancelledToast"));
        patchUpcoming(updated);
        setSelectedObservation(null);
      } catch (err) {
        toast.error(t("upcoming.cancelFailed"));
      }
    },
    [patchUpcoming, t],
  );

  const handleRescheduleObservation = useCallback(
    async (uuid, body) => {
      try {
        const updated = await updateObservation({ uuid, body });
        toast.success(t("upcoming.visitRescheduledToast"));
        patchUpcoming(updated);
        setSelectedObservation(null);
      } catch (err) {
        toast.error(slotErrorMessage(err, t, t("upcoming.rescheduleFailed")));
      }
    },
    [patchUpcoming, t],
  );

  const loadNotifications = useCallback(() => {
    if (!user?.uuid) return undefined;
    const controller = new AbortController();
    getNotifications({ params: { page_size: MAX_PAGE_SIZE }, signal: controller.signal })
      .then((data) => setNotifications(data?.results || []))
      .catch((err) => {
        if (err?.code === "ERR_CANCELED") return;
        setNotifications([]);
      });
    return () => controller.abort();
  }, [user?.uuid]);

  useEffect(() => loadNotifications(), [loadNotifications]);

  const markNotificationAsRead = useCallback((uuid) => {
    let previous;
    setNotifications((prev) => {
      previous = prev;
      return prev.map((n) =>
        n.uuid === uuid
          ? { ...n, is_read: true, read_at: new Date().toISOString() }
          : n,
      );
    });
    markNotificationRead({ uuid }).catch(() => {
      setNotifications(previous);
      toast.error(t("teacher.markReadFailed"));
    });
  }, [t]);

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) markNotificationAsRead(notification.uuid);
    if (notification.related_support_ticket) {
      navigate(`/support/${notification.related_support_ticket}`);
      return;
    }
    if (notification.related_observation) {
      const related = upcoming.find(
        (o) => o.uuid === notification.related_observation,
      );
      if (related) {
        setSelectedObservation(related);
        setShowNotifications(false);
      }
    }
  };

  const handleMarkAllRead = () => {
    let previous;
    setNotifications((prev) => {
      previous = prev;
      return prev.map((n) =>
        n.is_read ? n : { ...n, is_read: true, read_at: new Date().toISOString() },
      );
    });
    markAllNotificationsRead().catch(() => {
      setNotifications(previous);
      toast.error(t("teacher.markAllReadFailed"));
    });
  };

  // Refresh when opening (not closing); skip refetches inside a short window
  // to avoid a noisy storm if the user pumps the bell open/closed.
  useEffect(() => {
    if (!showNotifications) return;
    loadNotifications();
  }, [showNotifications, loadNotifications]);

  const handleToggleNotifications = () => setShowNotifications((open) => !open);

  const handleSubmittedVisit = () => {
    setRequestModal(null);
    loadUpcoming();
  };

  if (!user?.uuid || isLoading) {
    return (
      <div className="p-6 bg-slate-50 min-h-full">
        <div className="max-w-6xl mx-auto space-y-6">
          <Skeleton className="h-40 rounded-[32px] bg-slate-300/60" />
          <div className="flex gap-2">
            <Skeleton className="h-9 w-40 rounded-xl" />
            <Skeleton className="h-9 w-40 rounded-xl" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <SkeletonCard className="lg:col-span-2 h-80" lines={6} />
            <SkeletonCard className="h-80" lines={5} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-slate-50 min-h-full">
      <div className="max-w-6xl mx-auto">
        {/* overflow-clip (not -hidden): clips the hero's blur bleed to the
            rounded corners WITHOUT becoming a scrollport, so the Upcoming
            tab's sticky day headers stick against BaseLayout's <main>. */}
        <div className="bg-white rounded-[32px] shadow-xl border border-slate-200 overflow-clip">
          <div className="bg-slate-900 p-8 text-white relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full -mr-32 -mt-32 blur-3xl" />
            <div className="relative flex items-center justify-between flex-wrap gap-6">
              <div className="flex items-center gap-4 sm:gap-8 min-w-0">
                <Avatar
                  user={user}
                  alt={fullName(user)}
                  className="w-24 h-24 rounded-full bg-indigo-600 border-4 border-white text-white text-2xl font-black shadow-xl ring-1 ring-slate-200"
                />

                <div className="flex flex-col items-start gap-3">
                  <h2 className="text-2xl sm:text-4xl font-black text-white leading-none tracking-tight break-words">
                    {fullName(user)}
                  </h2>
                  {department && (
                    <span className="px-3 py-1 text-[10px] font-black uppercase rounded-full border border-white/20 bg-white/10 text-indigo-200 shadow-sm">
                      {department}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-10">
                <div className="relative">
                  <button
                    onClick={handleToggleNotifications}
                    className="relative p-3 rounded-2xl bg-white/10 hover:bg-white/20 border border-white/10"
                    aria-label={t("teacher.notifications")}
                  >
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                      />
                    </svg>
                    {unreadCount > 0 && (
                      <span className="absolute top-2 right-2 w-4 h-4 bg-rose-500 text-white text-[10px] font-black rounded-full flex items-center justify-center border-2 border-slate-900 animate-pulse">
                        {unreadCount}
                      </span>
                    )}
                  </button>

                  {showNotifications && (
                    <NotificationsDropdown
                      notifications={notifications}
                      unreadCount={unreadCount}
                      onNotificationClick={handleNotificationClick}
                      onMarkAllRead={handleMarkAllRead}
                      onClose={() => setShowNotifications(false)}
                    />
                  )}
                </div>

                <div className="relative flex items-center justify-center w-24 h-24">
                  <div
                    className={`absolute inset-0 rounded-full blur-2xl opacity-20 ${colors.glow}`}
                  />
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="transparent"
                      className="text-white/10"
                    />
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="transparent"
                      strokeDasharray={251.3}
                      strokeDashoffset={251.3 * (1 - (overallScore ?? 0) / 100)}
                      strokeLinecap="round"
                      className={`transition-all duration-1000 ease-out ${colors.stroke}`}
                    />
                  </svg>
                  <div className="absolute flex flex-col items-center text-center">
                    <span className="text-3xl font-black text-white leading-none tracking-tighter">
                      {overallScore == null ? (
                        "—"
                      ) : (
                        <>
                          {overallScore}
                          <span className="text-sm opacity-40">%</span>
                        </>
                      )}
                    </span>
                    <span className="text-[8px] font-black text-white/40 uppercase tracking-[0.15em] mt-1">
                      {t("teacher.overall")}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 mt-10">
              {[
                { label: t("teacher.employedSince"), value: employedSince },
                { label: t("teacher.subject"), value: subject },
                { label: t("teacher.grades"), value: inferGradesFromObservations(observations) || "—" },
                { label: t("teacher.lastObservation"), value: lastObservationDate, color: isLastObservationFresh ? "text-emerald-400" : "text-amber-400" },
                { label: t("teacher.lessonsObserved"), value: observationTotal ?? observations.length, color: "text-indigo-300" },
                { label: t("teacher.peersVisited"), value: peersVisitedCount, color: "text-emerald-300" },
              ].map((stat) => (
                <div key={stat.label}>
                  <p className="text-[10px] font-bold text-white/40 uppercase tracking-widest mb-1">
                    {stat.label}
                  </p>
                  <p className={`text-sm font-black ${stat.color || "text-white"}`}>
                    {stat.value}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="p-8 bg-slate-50/50 space-y-8">
            {observationsError && (
              <div className="flex items-center justify-between gap-4 p-4 rounded-2xl bg-rose-50 border border-rose-100">
                <p className="text-xs font-bold text-rose-700">
                  {t("teacher.observationsLoadFailed")}
                </p>
                <button
                  type="button"
                  onClick={loadObservations}
                  className="text-[10px] font-black text-rose-600 uppercase tracking-widest hover:text-rose-800 whitespace-nowrap"
                >
                  {t("common.retry")}
                </button>
              </div>
            )}
            <div className="flex items-center gap-2 p-1.5 bg-indigo-50/50 rounded-[24px] w-fit max-w-full overflow-x-auto border border-indigo-100/50">
              <button
                onClick={() => setActiveTab("overview")}
                className={`px-4 sm:px-8 py-3 rounded-[18px] text-[11px] font-black uppercase tracking-widest whitespace-nowrap transition-all duration-300 ${
                  activeTab === "overview"
                    ? "bg-white text-indigo-600 shadow-[0_8px_20px_-4px_rgba(79,70,229,0.15)] border border-indigo-50"
                    : "text-slate-400 hover:text-slate-600"
                }`}
              >
                {t("teacher.tabOverview")}
              </button>
              <button
                onClick={() => setActiveTab("upcoming")}
                className={`px-8 py-3 rounded-[18px] text-[11px] font-black uppercase tracking-widest transition-all duration-300 flex items-center gap-3 ${
                  activeTab === "upcoming"
                    ? "bg-white text-indigo-600 shadow-[0_8px_20px_-4px_rgba(79,70,229,0.15)] border border-indigo-50"
                    : "text-slate-400 hover:text-slate-600"
                }`}
              >
                {t("teacher.tabUpcoming")}
                <span
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black transition-colors ${
                    activeTab === "upcoming"
                      ? "bg-indigo-600 text-white"
                      : "bg-indigo-100 text-indigo-600"
                  }`}
                >
                  {upcoming.length}
                </span>
              </button>
            </div>

            {activeTab === "overview" ? (
              <PerformanceOverviewTab
                user={user}
                rubric={rubric}
                observationDetails={observationDetails}
                expandedStandardId={expandedStandardId}
                setExpandedStandardId={setExpandedStandardId}
                showFocusAreas={showFocusAreas}
                setShowFocusAreas={setShowFocusAreas}
                onRequestVisit={(peer) => setRequestModal({ peer })}
              />
            ) : (
              <UpcomingObservationsTab
                observations={upcoming}
                timetable={timetable}
                groups={groups}
                rubric={rubric}
                onTimetableChanged={loadTimetable}
                calendarViewMode={calendarViewMode}
                setCalendarViewMode={setCalendarViewMode}
                onSelectObservation={setSelectedObservation}
                onConfirm={handleConfirmObservation}
                onDecline={handleDeclineObservation}
                onRequestVisit={(slot) => setRequestModal({ slot })}
              />
            )}
          </div>
        </div>
      </div>

      {requestModal && (
        <RequestVisitModal
          onClose={() => setRequestModal(null)}
          onSubmitted={handleSubmittedVisit}
          currentUser={user}
          rubricVersionUuid={rubric?.uuid}
          rubric={rubric}
          initial={requestModal.peer
            ? { peer: requestModal.peer }
            : requestModal.slot || {}}
        />
      )}
      {selectedObservation && (
        <ObservationDetailModal
          observation={selectedObservation}
          rubric={rubric}
          onClose={() => setSelectedObservation(null)}
          onConfirm={handleConfirmObservation}
          onDecline={handleDeclineObservation}
          onCancel={handleCancelObservation}
          onReschedule={handleRescheduleObservation}
        />
      )}
    </div>
  );
}
